import { useEffect, useMemo, useState } from "react";
import type {
  CustomOrder,
  Profile,
  ProfileLinkConfig,
  WorkplaceItem,
} from "../../../../../client";
import { BulletSectionTitle } from "../../../../../components";
import { Workplace } from "./Workplace";
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { createPortal } from "react-dom";
import { useStatsigClient } from "../../../../../hooks";
import { useThemeColors } from "../../../../../contexts";

interface Props {
  profileData?: Profile;
  linkConfigData: ProfileLinkConfig;
  handleUpdateLink: (data: Partial<ProfileLinkConfig>) => void;
  isDefaultLead: boolean;
}

interface SortableWorkplaceProps {
  workplace: WorkplaceItem;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  showSkills?: boolean;
  showDescription?: boolean;
  isEditiable: boolean;
  onWorkplaceEdit: (description: string) => void;
  isEdited: boolean;
  isDragEnabled: boolean;
  showIcons?: boolean;
  isDefaultLead: boolean;
}

const SortableWorkplace = ({
  workplace,
  checked,
  onCheckedChange,
  showSkills,
  showDescription,
  isEditiable,
  onWorkplaceEdit,
  isEdited,
  isDragEnabled,
  showIcons,
  isDefaultLead,
}: SortableWorkplaceProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: workplace.id,
    disabled: !isDragEnabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="touch-none">
      <div
        {...attributes}
        {...listeners}
        className={isDragEnabled ? "cursor-grab" : ""}
      >
        <Workplace
          key={workplace.id}
          isEditable={isEditiable}
          checked={checked}
          onCheckedChange={onCheckedChange}
          name={workplace.name}
          skillTags={workplace.skillTags}
          role={workplace.role}
          description={workplace.description}
          startDate={workplace.startDate}
          endDate={workplace.endDate}
          showSkills={showSkills}
          showSkillIcons={showIcons}
          showDescription={showDescription}
          isEditiable={isEditiable}
          onWorkplaceEdit={onWorkplaceEdit}
          isEdited={isEdited}
          isDefaultLead={isDefaultLead}
        />
      </div>
    </div>
  );
};

export const LinkCreatorWork = ({
  profileData,
  linkConfigData,
  handleUpdateLink,
  isDefaultLead,
}: Props) => {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [activeWorkplace, setActiveWorkplace] = useState<WorkplaceItem | null>(
    null,
  );
  const [localWorkplaces, setLocalWorkplaces] = useState<WorkplaceItem[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const statsigClient = useStatsigClient();
  const isDragEnabled =
    (statsigClient?.checkGate("enable_workplace_drag_and_drop") ?? false) &&
    !isDefaultLead;

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
  );

  const { secondary } = useThemeColors();

  const sortedWorkplaces = useMemo(() => {
    const editedWorkplaces = linkConfigData?.editedWorkplaces ?? [];
    let workplaces =
      profileData?.workplaces?.map((wp) => {
        const editedWorkplace = editedWorkplaces.find((w) => w.id === wp.id);
        return editedWorkplace ?? wp;
      }) || [];

    // Apply custom ordering if it exists
    if (
      linkConfigData?.customWorkplaceOrder &&
      linkConfigData.customWorkplaceOrder.length > 0
    ) {
      const orderMap = new Map<string, number>();
      linkConfigData.customWorkplaceOrder.forEach((item, index) => {
        orderMap.set(item.id, index);
      });

      // Sort workplaces based on the custom order
      workplaces = [...workplaces].sort((a, b) => {
        const orderA = orderMap.has(a.id)
          ? orderMap.get(a.id)!
          : Number.MAX_SAFE_INTEGER;
        const orderB = orderMap.has(b.id)
          ? orderMap.get(b.id)!
          : Number.MAX_SAFE_INTEGER;
        return orderA - orderB;
      });
    }

    return workplaces;
  }, [
    profileData?.workplaces,
    linkConfigData?.editedWorkplaces,
    linkConfigData?.customWorkplaceOrder,
  ]);

  // Update local workplaces when sorted workplaces change and we're not dragging
  useEffect(() => {
    if (!isDragging && sortedWorkplaces) {
      setLocalWorkplaces(sortedWorkplaces);
    }
  }, [sortedWorkplaces, isDragging]);

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    setActiveId(active.id.toString());
    setIsDragging(true);

    const draggedWorkplace = localWorkplaces?.find(
      (workplace) => workplace.id === active.id,
    );
    if (draggedWorkplace) {
      setActiveWorkplace(draggedWorkplace);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    setActiveId(null);
    setActiveWorkplace(null);

    if (!over || !localWorkplaces) {
      setIsDragging(false);
      return;
    }

    const activeIndex = localWorkplaces.findIndex(
      (workplace) => workplace.id === active.id,
    );
    const overIndex = localWorkplaces.findIndex(
      (workplace) => workplace.id === over.id,
    );

    if (activeIndex === overIndex) {
      setIsDragging(false);
      return;
    }

    // Create a new array with the updated order
    const updatedWorkplaces = [...localWorkplaces];
    const [movedItem] = updatedWorkplaces.splice(activeIndex, 1);
    updatedWorkplaces.splice(overIndex, 0, movedItem);

    // Update our local state immediately
    setLocalWorkplaces(updatedWorkplaces);

    // Create the new custom order based on the drag operation
    const newOrder: CustomOrder[] = updatedWorkplaces.map((workplace) => ({
      id: workplace.id,
      name: workplace.name,
    }));

    // Update the linkConfigData with the new custom order
    handleUpdateLink({ customWorkplaceOrder: newOrder });

    // Reset dragging state after a short delay to ensure UI updates properly
    setTimeout(() => {
      setIsDragging(false);
    }, 100);
  };

  const handleCheckedChange = (checked: boolean, workplaceId: string) => {
    const currentIds = new Set(
      linkConfigData.profileLinkWorkplaceIds ??
        profileData?.workplaces.map((workplace) => workplace.id),
    );

    if (checked) {
      currentIds.add(workplaceId);
    } else {
      currentIds.delete(workplaceId);
    }

    handleUpdateLink({ profileLinkWorkplaceIds: Array.from(currentIds) });
  };

  const handleUpdatedWorkplace = (workplace: WorkplaceItem) => {
    if (!linkConfigData) {
      return;
    }

    if (!linkConfigData.editedWorkplaces) {
      linkConfigData.editedWorkplaces = [];
    }

    var currentEditedWorkplaces = [...linkConfigData.editedWorkplaces];

    var found = false;
    for (var i = 0; i < currentEditedWorkplaces.length; i++) {
      if (currentEditedWorkplaces[i].id === workplace.id) {
        currentEditedWorkplaces[i] = workplace;
        found = true;
      }
    }

    if (!found) {
      currentEditedWorkplaces.push(workplace);
    }

    handleUpdateLink({ editedWorkplaces: currentEditedWorkplaces });
  };

  return (
    profileData?.workplaces && (
      <div className="flex flex-col gap-4">
        <div style={{ "--text-color": secondary } as React.CSSProperties}>
          <BulletSectionTitle childrenClassName="text-[var(--text-color)]">
            Work experience
          </BulletSectionTitle>
        </div>
        <div className="flex flex-col gap-6">
          {isDragEnabled ? (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={localWorkplaces.map((workplace) => workplace.id)}
                strategy={verticalListSortingStrategy}
              >
                {localWorkplaces.map((workplace) => (
                  <SortableWorkplace
                    key={workplace.id}
                    workplace={workplace}
                    checked={
                      !linkConfigData.profileLinkWorkplaceIds ||
                      linkConfigData.profileLinkWorkplaceIds?.includes(
                        workplace.id,
                      )
                    }
                    onCheckedChange={(checked) =>
                      handleCheckedChange(checked, workplace.id)
                    }
                    showSkills={
                      linkConfigData.workplaceLinkConfig?.displaySkills
                    }
                    showDescription={
                      linkConfigData.workplaceLinkConfig?.displayDescription
                    }
                    isEditiable={!isDefaultLead}
                    onWorkplaceEdit={(description: string) => {
                      var updatedWorkplace = { ...workplace };
                      updatedWorkplace.description = description;
                      handleUpdatedWorkplace(updatedWorkplace);
                    }}
                    isEdited={
                      linkConfigData?.editedWorkplaces?.find(
                        (w) => w.id === workplace.id,
                      ) !== undefined
                    }
                    isDragEnabled={isDragEnabled}
                    showIcons={
                      linkConfigData.workplaceLinkConfig?.displaySkillIcons ??
                      true
                    }
                    isDefaultLead={isDefaultLead}
                  />
                ))}
              </SortableContext>
              {createPortal(
                <DragOverlay>
                  {activeId && activeWorkplace ? (
                    <Workplace
                      isEditable={true}
                      checked={
                        !linkConfigData.profileLinkWorkplaceIds ||
                        linkConfigData.profileLinkWorkplaceIds?.includes(
                          activeWorkplace.id,
                        )
                      }
                      onCheckedChange={() => {}}
                      name={activeWorkplace.name}
                      skillTags={activeWorkplace.skillTags}
                      role={activeWorkplace.role}
                      description={activeWorkplace.description}
                      startDate={activeWorkplace.startDate}
                      endDate={activeWorkplace.endDate}
                      showSkills={
                        linkConfigData.workplaceLinkConfig?.displaySkills ??
                        false
                      }
                      showSkillIcons={
                        linkConfigData.workplaceLinkConfig?.displaySkillIcons ??
                        true
                      }
                      showDescription={
                        linkConfigData.workplaceLinkConfig
                          ?.displayDescription ?? false
                      }
                      isEditiable={false}
                      onWorkplaceEdit={() => {}}
                      isEdited={false}
                      isDefaultLead={isDefaultLead}
                    />
                  ) : null}
                </DragOverlay>,
                document.body,
              )}
            </DndContext>
          ) : (
            <>
              {sortedWorkplaces.map((workplace) => (
                <Workplace
                  key={workplace.id}
                  isEditable={true}
                  checked={
                    !linkConfigData.profileLinkWorkplaceIds ||
                    linkConfigData.profileLinkWorkplaceIds?.includes(
                      workplace.id,
                    )
                  }
                  onCheckedChange={(checked) =>
                    handleCheckedChange(checked, workplace.id)
                  }
                  name={workplace.name}
                  skillTags={workplace.skillTags}
                  role={workplace.role}
                  description={workplace.description}
                  startDate={workplace.startDate}
                  endDate={workplace.endDate}
                  showSkills={
                    linkConfigData.workplaceLinkConfig?.displaySkills ?? false
                  }
                  showSkillIcons={
                    linkConfigData.workplaceLinkConfig?.displaySkillIcons ??
                    true
                  }
                  showDescription={
                    linkConfigData.workplaceLinkConfig?.displayDescription ??
                    false
                  }
                  isEditiable={true}
                  onWorkplaceEdit={(description: string) => {
                    var updatedWorkplace = { ...workplace };
                    updatedWorkplace.description = description;
                    handleUpdatedWorkplace(updatedWorkplace);
                  }}
                  isEdited={
                    linkConfigData?.editedWorkplaces?.find(
                      (w) => w.id === workplace.id,
                    ) !== undefined
                  }
                  isDefaultLead={isDefaultLead}
                />
              ))}
            </>
          )}
        </div>
      </div>
    )
  );
};
